/**
 * Infiltration Run — screen orchestrator.
 * Routes intro / event / extraction / result, binds events, animates the
 * detection gauge. HTML is built by infiltrationRender + infiltrationEventRender.
 */

import { t } from '../i18n/index.js';
import {
  getState, getViewState, dispatch, onRender, markScreenDirty, requestRender, showAdRewardPopup,
} from './renderer.js';
import { AdManager } from '../monetization/index.js';
import { introScreenHtml, resultScreenHtml } from './infiltrationRender.js';
import { eventScreenHtml, extractionScreenHtml } from './infiltrationEventRender.js';

let lastRenderKey = '';
let prevGaugePct = 0;

export function initInfiltrationScreen(): void {
  onRender(renderInfiltrationScreen);
  onRender(tickInfiltrationScreen, { alwaysTabs: ['infiltration'] });
}

/** Live-patch the cooldown countdown so the intro button re-enables on time. */
function tickInfiltrationScreen(): void {
  if (getViewState().currentTab !== 'infiltration') return;
  const inf = getState().infiltration;
  if (inf.run) return;
  const left = inf.cooldownUntil - Date.now();
  if (left > 0) {
    const cdEl = document.getElementById('infil-cooldown');
    if (cdEl) cdEl.textContent = `${Math.ceil(left / 1000)}s`;
  } else {
    const btn = document.getElementById('infil-start') as HTMLButtonElement | null;
    if (btn && btn.disabled) { markScreenDirty(); requestRender(); }
  }
}

/** Build a key capturing everything that changes the DOM layout/content. */
function renderKey(): string {
  const inf = getState().infiltration;
  const run = inf.run;
  if (!run) {
    const cd = inf.cooldownUntil - Date.now() > 0 ? 'cd' : 'ready';
    return `intro:${inf.tutorialDone}:${inf.totalRuns}:${inf.totalSuccess}:${inf.highestCareer}:${cd}`;
  }
  const phase = run.finished
    ? `res:${run.finalOutcome}:${run.retryUsed}`
    : run.lastChoiceResult
      ? `reveal:${run.lastChoiceResult.choiceId}:${run.lastChoiceResult.success}`
      : run.currentIndex >= run.eventQueue.length
        ? 'extract'
        : `event:${run.currentIndex}`;
  return `${phase}:${Math.round(run.detection)}`;
}

function renderInfiltrationScreen(): void {
  if (getViewState().currentTab !== 'infiltration') return;
  const container = document.getElementById('screen-content');
  if (!container) return;

  // If another tab overwrote the container, force a rebuild.
  if (!container.querySelector('.infil-screen')) lastRenderKey = '';

  const key = renderKey();
  if (key === lastRenderKey) return;
  lastRenderKey = key;

  const inf = getState().infiltration;
  const run = inf.run;

  if (!run) {
    const left = Math.max(0, inf.cooldownUntil - Date.now());
    container.innerHTML = introScreenHtml(inf, left);
    prevGaugePct = 0;
    bindIntro();
    return;
  }
  if (run.finished) {
    container.innerHTML = resultScreenHtml(run);
    prevGaugePct = run.detection;
    bindResult();
    return;
  }
  if (run.currentIndex >= run.eventQueue.length) {
    container.innerHTML = extractionScreenHtml(run, prevGaugePct);
    animateGauge(run.detection);
    bindExtraction();
    return;
  }
  container.innerHTML = eventScreenHtml(run, prevGaugePct);
  animateGauge(run.detection);
  bindEvent();
}

/** Animate the detection gauge fill/label from prevGaugePct to target. */
function animateGauge(target: number): void {
  requestAnimationFrame(() => {
    const fill = document.getElementById('infil-gauge-fill') as HTMLElement | null;
    const val = document.getElementById('infil-gauge-val');
    if (fill) {
      fill.style.width = `${target}%`;
      fill.className = `infil-gauge-fill band-${target < 40 ? 'low' : target < 70 ? 'mid' : 'high'}`;
    }
    if (val) val.textContent = `${Math.round(target)}%`;
  });
  prevGaugePct = target;
}

function bindIntro(): void {
  document.getElementById('infil-start')?.addEventListener('click', () => {
    dispatch({ type: 'START_INFILTRATION' });
  });
}

function bindEvent(): void {
  document.querySelectorAll('.infil-choice[data-choice]').forEach((el) => {
    el.addEventListener('click', () => {
      const choiceId = (el as HTMLElement).dataset.choice;
      if (choiceId) dispatch({ type: 'CHOOSE_INFILTRATION_OPTION', payload: { choiceId } });
    });
  });
  document.getElementById('infil-next')?.addEventListener('click', () => {
    dispatch({ type: 'ADVANCE_INFILTRATION' });
  });
}

function bindExtraction(): void {
  document.getElementById('infil-extract')?.addEventListener('click', () => {
    dispatch({ type: 'RESOLVE_INFILTRATION' });
  });
}

function bindResult(): void {
  document.getElementById('infil-close')?.addEventListener('click', () => {
    dispatch({ type: 'CLOSE_INFILTRATION' });
  });
  document.getElementById('infil-retry')?.addEventListener('click', () => openRetryAd());
}

/** Opt-in dialog first (AdMob policy), then the rewarded ad, then the retry. */
function openRetryAd(): void {
  showAdRewardPopup(
    t('infil_retry_title'),
    t('infil_retry_desc'),
    () => {
      void AdManager.showRewardedAd(
        { type: 'boost', amount: 0, description: t('infil_retry_desc') },
        () => { dispatch({ type: 'AD_INFILTRATION_RETRY' }); },
        () => { /* ad unavailable — keep the result screen as-is */ },
      );
    },
    t('infil_retry_confirm'),
    t('infil_cancel'),
  );
}
